import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { throttle } from 'lodash';
import {
    MdNavigateBefore,
    MdNavigateNext,
} from 'react-icons/md';

export default function HorizontalScrollArrows({
    scrollableElement,
    durationMs,
    scrollAmountPx: scrollAmountPxProp,
    scrollAmountMobilePx,
    alignRight,
    isSkeleton,
}) {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isFullyScrolled, setIsFullyScrolled] = useState(false);
    const [isScrollingAnimationOngoing, setIsScrollingAnimationOngoing] = useState(false);
    const [isContentScrollable, setIsContentScrollable] = useState(true);
    const [isMobileScreen, setIsMobileScreen] = useState(true);
    const scrollAmountPx = isMobileScreen ? (scrollAmountMobilePx ?? scrollAmountPxProp) : scrollAmountPxProp;

    const handleScroll = useCallback(() => {
        if (!scrollableElement) return;
        if (isScrollingAnimationOngoing) return;

        setIsScrolled(scrollableElement.scrollLeft > 0);
        setIsFullyScrolled(
            Math.ceil(scrollableElement.scrollLeft + scrollableElement.clientWidth)
                >= scrollableElement.scrollWidth,
        );
    }, [scrollableElement, isScrollingAnimationOngoing]);

    const throttledHandleScroll = useMemo(
        () => throttle(handleScroll, 100),
        [handleScroll],
    );

    useEffect(
        () => () => {
            throttledHandleScroll.cancel();
        },
        [throttledHandleScroll],
    );

    useEffect(() => {
        if (!scrollableElement) return undefined;
        const handleResize = () => {
            setIsMobileScreen(window.innerWidth < 992);
            setIsContentScrollable(scrollableElement.scrollWidth > scrollableElement.clientWidth);
            setIsScrolled(scrollableElement.scrollLeft > 0);
            setIsFullyScrolled(
                Math.ceil(scrollableElement.scrollLeft + scrollableElement.clientWidth)
                    >= scrollableElement.scrollWidth,
            );
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        scrollableElement.addEventListener('scroll', throttledHandleScroll);

        return () => {
            window.removeEventListener('resize', handleResize);
            scrollableElement?.removeEventListener('scroll', throttledHandleScroll);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollableElement]);

    const easeInOut = (t) => {
        if (t < 0.5) {
            return 2 * t * t;
        }
        return -1 + (4 - 2 * t) * t;
    };

    const scrollSmoothly = useCallback((direction) => {
        if (!scrollableElement) return;

        const startScrollLeft = scrollableElement.scrollLeft;
        let distance = 0;
        if (direction === 'left' && !isScrolled) {
            distance = scrollableElement.scrollWidth - scrollableElement.clientWidth - scrollableElement.scrollLeft;
        } else if (direction === 'right' && isFullyScrolled) {
            distance = -scrollableElement.scrollLeft;
        } else {
            distance = direction === 'right' ? scrollAmountPx : -scrollAmountPx;
        }

        setIsScrollingAnimationOngoing(true);
        let startTime = null;
        const animateScroll = (timestamp) => {
            if (!startTime) {
                startTime = timestamp;
            }
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / durationMs, 1);
            scrollableElement.scrollLeft = (
                startScrollLeft + distance * easeInOut(progress)
            );
            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            } else {
                setIsScrollingAnimationOngoing(false);
                setIsScrolled(scrollableElement.scrollLeft > 0);
                setIsFullyScrolled(
                    Math.ceil(scrollableElement.scrollLeft + scrollableElement.clientWidth)
                        >= scrollableElement.scrollWidth,
                );
            }
        };

        requestAnimationFrame(animateScroll);
    }, [scrollableElement, scrollAmountPx, durationMs, isScrolled, isFullyScrolled]);

    const handleLeftArrowClick = () => {
        scrollSmoothly('left');
    };

    const handleRightArrowClick = () => {
        scrollSmoothly('right');
    };

    return (isContentScrollable || isSkeleton) && (
        <div className={`horizontal-scroll-arrows ${alignRight ? 'horizontal-scroll-arrows--align-right' : ''} ${isSkeleton ? 'horizontal-scroll-arrows--skeleton' : ''}`}>
            <button
                type="button"
                className="horizontal-scroll-arrow horizontal-scroll-arrow--left"
                onClick={handleLeftArrowClick}
                aria-label="Scroll left"
                disabled={!isScrolled}
            >
                <MdNavigateBefore className="horizontal-scroll-arrow__icon" />
            </button>
            <button
                type="button"
                className="horizontal-scroll-arrow horizontal-scroll-arrow--right"
                onClick={handleRightArrowClick}
                aria-label="scroll right"
                disabled={isFullyScrolled}
            >
                <MdNavigateNext className="horizontal-scroll-arrow__icon" />
            </button>
        </div>
    );
}
